import { type Constraint, type ConstraintId, entitiesOf } from '../constraints/types';
import { parentsOf } from '../construction/source';

import { type Entity } from './entities';
import { type EntityId } from './ids';

/**
 * The saved document.
 *
 * Everything a file contains and nothing more. Evaluated coordinates, bounding boxes,
 * the spatial index and measurement results are all absent by design — they are derived
 * from this and rebuilt on load.
 *
 * Entities are held in an insertion-ordered map keyed by id rather than an array. Lookup
 * by id is the dominant access pattern (every parent reference is a lookup), and a
 * `Map` preserves insertion order, so iteration remains deterministic — which the
 * serializer needs for stable output and replay needs for reproducibility.
 */
export interface SceneDocument {
  readonly entities: ReadonlyMap<EntityId, Entity>;

  /**
   * Tier-2 relational constraints (ADR-0003).
   *
   * Held separately from entities because a constraint is not a thing on the canvas — it
   * has no geometry, no z-order and no style. Keeping it out of the entity map also keeps
   * the construction DAG free of edges that are not construction edges.
   */
  readonly constraints: ReadonlyMap<ConstraintId, Constraint>;

  /** Next value for the id generator, so ids are never reused across a save/reload. */
  readonly nextId: number;

  readonly title: string;

  /** Free-form metadata a publisher or LMS may attach. Preserved verbatim on save. */
  readonly metadata: Readonly<Record<string, string>>;
}

export function createDocument(title = 'Untitled'): SceneDocument {
  return {
    entities: new Map<EntityId, Entity>(),
    constraints: new Map<ConstraintId, Constraint>(),
    nextId: 1,
    title,
    metadata: {},
  };
}

export function getEntity(doc: SceneDocument, id: EntityId): Entity | undefined {
  return doc.entities.get(id);
}

/** Throws when the entity is missing. Use where absence indicates a corrupt document. */
export function requireEntity(doc: SceneDocument, id: EntityId): Entity {
  const entity = doc.entities.get(id);
  if (entity === undefined) {
    throw new Error(`EduSketch: entity "${id}" not found in document`);
  }
  return entity;
}

export function hasEntity(doc: SceneDocument, id: EntityId): boolean {
  return doc.entities.has(id);
}

/**
 * Entities in draw order: ascending `zIndex`, ties broken by insertion order.
 *
 * The tie-break matters — without it, two entities created with the same z would swap
 * places between runs, and a rendering snapshot test would flicker.
 */
export function inDrawOrder(doc: SceneDocument): readonly Entity[] {
  const cached = drawOrderCache.get(doc);
  if (cached !== undefined) return cached;

  const sorted = [...doc.entities.values()]
    .map((entity, index) => ({ entity, index }))
    .sort((a, b) => a.entity.zIndex - b.entity.zIndex || a.index - b.index)
    .map(({ entity }) => entity);

  drawOrderCache.set(doc, sorted);
  return sorted;
}

/**
 * Each entity's position in draw order.
 *
 * Hit-testing needs one entity's rank, not the whole ordered list, and building the list to
 * find it made every pointer move pay an `O(n log n)` sort — 0.3 ms on an 8 000-entity
 * document, on a path that runs at pointer rate.
 */
export function drawOrderOf(doc: SceneDocument): ReadonlyMap<EntityId, number> {
  const cached = rankCache.get(doc);
  if (cached !== undefined) return cached;

  const ranks = new Map<EntityId, number>();
  inDrawOrder(doc).forEach((entity, rank) => ranks.set(entity.id, rank));

  rankCache.set(doc, ranks);
  return ranks;
}

/**
 * Memoised because documents are immutable.
 *
 * Every edit produces a new `SceneDocument`, so a cache keyed on document identity can never
 * go stale — and a `WeakMap` means the entry dies with the document rather than holding every
 * undo state alive. This is the one place in the codebase where immutability is spent on
 * something other than undo, and it is worth more here.
 */
const drawOrderCache = new WeakMap<SceneDocument, readonly Entity[]>();
const rankCache = new WeakMap<SceneDocument, ReadonlyMap<EntityId, number>>();

/* ------------------------------------------------------------ structural queries */

/**
 * Entities that directly depend on `id`.
 *
 * Linear in document size. The engine maintains an incremental reverse index for the
 * hot path in M5; this is the definition that index is checked against.
 */
export function dependentsOf(doc: SceneDocument, id: EntityId): readonly EntityId[] {
  const result: EntityId[] = [];
  for (const entity of doc.entities.values()) {
    if (parentsOf(entity.source).includes(id)) result.push(entity.id);
  }
  return result;
}

/** Every entity reachable downstream of `id`, including `id` itself. */
export function transitiveDependentsOf(doc: SceneDocument, id: EntityId): ReadonlySet<EntityId> {
  const seen = new Set<EntityId>([id]);
  const queue: EntityId[] = [id];

  while (queue.length > 0) {
    const current = queue.pop();
    if (current === undefined) break;
    for (const dependent of dependentsOf(doc, current)) {
      if (seen.has(dependent)) continue;
      seen.add(dependent);
      queue.push(dependent);
    }
  }
  return seen;
}

/**
 * Ids referenced by some entity but absent from the document.
 *
 * A non-empty result means the document is corrupt. Checked on load rather than trusted:
 * a dangling parent would otherwise surface much later as an entity that silently stops
 * updating.
 */
export function danglingReferences(doc: SceneDocument): readonly EntityId[] {
  const missing = new Set<EntityId>();
  for (const entity of doc.entities.values()) {
    for (const parent of parentsOf(entity.source)) {
      if (!doc.entities.has(parent)) missing.add(parent);
    }
  }
  return [...missing];
}

/**
 * Topological order: every entity appears after all of its parents.
 *
 * Kahn's algorithm. Returns `null` when the graph contains a cycle, which the editor
 * must reject at construction time — a point that depends on itself has no value, and
 * the error a student needs is "this would make the point depend on itself", not a
 * stack overflow during evaluation.
 *
 * Ties are broken by insertion order so the result is deterministic, which "replay
 * construction" depends on.
 */
export function topologicalOrder(doc: SceneDocument): readonly EntityId[] | null {
  const inDegree = new Map<EntityId, number>();
  const children = new Map<EntityId, EntityId[]>();

  for (const entity of doc.entities.values()) {
    inDegree.set(entity.id, 0);
  }

  for (const entity of doc.entities.values()) {
    for (const parent of parentsOf(entity.source)) {
      if (!doc.entities.has(parent)) continue; // dangling refs reported separately
      inDegree.set(entity.id, (inDegree.get(entity.id) ?? 0) + 1);
      const list = children.get(parent);
      if (list === undefined) {
        children.set(parent, [entity.id]);
      } else {
        list.push(entity.id);
      }
    }
  }

  // Seeded in insertion order, and children are appended in insertion order, so the
  // whole traversal is deterministic.
  const ready: EntityId[] = [];
  for (const entity of doc.entities.values()) {
    if (inDegree.get(entity.id) === 0) ready.push(entity.id);
  }

  const ordered: EntityId[] = [];
  while (ready.length > 0) {
    const current = ready.shift();
    if (current === undefined) break;
    ordered.push(current);

    for (const child of children.get(current) ?? []) {
      const remaining = (inDegree.get(child) ?? 0) - 1;
      inDegree.set(child, remaining);
      if (remaining === 0) ready.push(child);
    }
  }

  return ordered.length === doc.entities.size ? ordered : null;
}

/** True when adding an edge from `parent` to `child` would close a cycle. */
export function wouldCreateCycle(doc: SceneDocument, child: EntityId, parent: EntityId): boolean {
  if (child === parent) return true;
  return transitiveDependentsOf(doc, child).has(parent);
}

/* -------------------------------------------------------------- immutable updates */

/**
 * All mutation helpers return a new document.
 *
 * The `entities` map is copied rather than mutated in place. That is what makes
 * `/rewind`, undo and the React store's change detection work by reference comparison,
 * and at the document sizes this editor targets the copy is not the bottleneck — the
 * geometry evaluation downstream of it is.
 */
export function addEntity(doc: SceneDocument, entity: Entity): SceneDocument {
  const entities = new Map(doc.entities);
  entities.set(entity.id, entity);
  return { ...doc, entities, nextId: Math.max(doc.nextId, extractCounter(entity.id) + 1) };
}

export function updateEntity(doc: SceneDocument, entity: Entity): SceneDocument {
  if (!doc.entities.has(entity.id)) {
    throw new Error(`EduSketch: cannot update missing entity "${entity.id}"`);
  }
  const entities = new Map(doc.entities);
  entities.set(entity.id, entity);
  return { ...doc, entities };
}

/**
 * Removes an entity **and everything that depends on it**.
 *
 * Cascading is not optional: leaving a midpoint whose segment is gone would produce an
 * entity that cannot be evaluated. Returns the ids actually removed so the caller can
 * build the inverse patch for undo.
 */
export function removeEntity(
  doc: SceneDocument,
  id: EntityId
): { readonly document: SceneDocument; readonly removed: readonly EntityId[] } {
  if (!doc.entities.has(id)) return { document: doc, removed: [] };

  const doomed = transitiveDependentsOf(doc, id);
  const entities = new Map(doc.entities);
  for (const victim of doomed) entities.delete(victim);

  // Constraints about deleted entities go with them: an equation whose subject no
  // longer exists can never be satisfied and would stall every subsequent solve.
  const constraints = new Map(doc.constraints);
  for (const constraintId of constraintsReferencing(doc, doomed)) {
    constraints.delete(constraintId);
  }

  return { document: { ...doc, entities, constraints }, removed: [...doomed] };
}

/* ------------------------------------------------------------------- constraints */

export function addConstraint(doc: SceneDocument, constraint: Constraint): SceneDocument {
  const constraints = new Map(doc.constraints);
  constraints.set(constraint.id, constraint);
  return { ...doc, constraints };
}

export function removeConstraint(doc: SceneDocument, id: ConstraintId): SceneDocument {
  if (!doc.constraints.has(id)) return doc;
  const constraints = new Map(doc.constraints);
  constraints.delete(id);
  return { ...doc, constraints };
}

/**
 * Constraints referring to any of `ids`.
 *
 * Used when deleting entities: a constraint whose subject no longer exists must go with
 * it, or the solver would keep trying to satisfy an equation about nothing.
 */
export function constraintsReferencing(
  doc: SceneDocument,
  ids: ReadonlySet<EntityId>
): readonly ConstraintId[] {
  const result: ConstraintId[] = [];
  for (const constraint of doc.constraints.values()) {
    for (const entity of entitiesOf(constraint)) {
      if (ids.has(entity)) {
        result.push(constraint.id);
        break;
      }
    }
  }
  return result;
}

/** Reads the numeric suffix of an id like `seg12`. Returns 0 when there is none. */
function extractCounter(id: EntityId): number {
  const match = /(\d+)$/.exec(id);
  if (match?.[1] === undefined) return 0;
  return Number.parseInt(match[1], 10);
}
